import * as React from "react";
import { useState, useMemo } from "react";
import { useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box, Card, CardContent, CardActions, Typography, Button, Chip,
  Grid, Avatar, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Collapse, Container, TextField,
  Alert, Tabs, Tab, Badge, LinearProgress, Tooltip,
} from "@mui/material";
import {
  ElectricBolt, LocalFireDepartment, Water, Build, Delete,
  CheckCircle, Cancel, Warning, ExpandMore, ExpandLess,
  CalendarToday, AttachMoney, Message, AccountBalance,
  NotificationsActive, HourglassTop, VerifiedUser, MoneyOff,
  TrendingUp, InfoOutlined, AccessTime, Schedule,
} from "@mui/icons-material";
import Wrapper       from "../../../components/Wrapper";
import title        from "../../../components/title";
import { GetBill }  from "../../../redux/reducer/GetBillDetails";
import { BASE_URL } from "../../../config/config";
import "./PendingBillStyles.css";
import TenantInfoBanner from "../../../components/TenantInfoBannerOwner";
import { BILL_TYPES, BACKEND_KEY } from "../../../constants/billTypes";

// ─────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────
const daysUntilDue = (dueDateStr) => {
  if (!dueDateStr) return null;
  const due = moment(dueDateStr, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
  if (!due.isValid()) return null;
  return due.diff(moment().startOf("day"), "days");
};

const getBillCategory = (bill) => {
  const status             = bill.approval?.status || "pending";
  const uploadedByLandlord = bill?.createdBy?.role === "landlord";
  const tenantUploaded     = bill?.createdBy?.role === "tenant";

  if (status === "approved") return "paid";
  if (status === "rejected") return "rejected";
  if (status === "pending" && (tenantUploaded || bill?.tenantPayment?.amount)) return "awaiting";
  if (uploadedByLandlord && status === "pending") return "unpaid";
  return "other";
};

const fmtCurrency = (n) =>
  `Rs. ${(parseFloat(n) || 0).toLocaleString("en-PK")}`;

// Keyed by the backend's own field spelling (BACKEND_KEY values) so this
// stays the single place that knowledge lives, without changing the
// `billType` values this file already produces/consumes everywhere else.
const BILL_META = {
  [BACKEND_KEY[BILL_TYPES.ELECTRICITY]]:  { color: "#F97316", gradient: "linear-gradient(135deg,#FF8C42 0%,#F97316 50%,#EA580C 100%)", label: "Electricity" },
  [BACKEND_KEY[BILL_TYPES.GAS]]:          { color: "#EF4444", gradient: "linear-gradient(135deg,#F87171 0%,#EF4444 50%,#DC2626 100%)", label: "SSGC Gas"    },
  [BACKEND_KEY[BILL_TYPES.WATER]]:        { color: "#3B82F6", gradient: "linear-gradient(135deg,#60A5FA 0%,#3B82F6 50%,#2563EB 100%)", label: "Water"       },
  [BACKEND_KEY[BILL_TYPES.MAINTENANCE]]:  { color: "#8B5CF6", gradient: "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 50%,#7C3AED 100%)", label: "Maintenance" },
  [BACKEND_KEY[BILL_TYPES.SECURITY]]:     { color: "#6366F1", gradient: "linear-gradient(135deg,#818CF8 0%,#6366F1 50%,#4F46E5 100%)", label: "Security"    },
  [BACKEND_KEY[BILL_TYPES.TRASH]]:        { color: "#10B981", gradient: "linear-gradient(135deg,#34D399 0%,#10B981 50%,#059669 100%)", label: "Waste Mgmt"  },
  [BACKEND_KEY[BILL_TYPES.RENT]]:         { color: "#7C3AED", gradient: "linear-gradient(135deg,#A78BFA 0%,#7C3AED 50%,#6D28D9 100%)", label: "Rent"        },
  [BACKEND_KEY[BILL_TYPES.FIXED]]:        { color: "#0D9488", gradient: "linear-gradient(135deg,#2DD4BF 0%,#0D9488 50%,#0F766E 100%)", label: "Fixed Charges" },
};
const getBillMeta  = (t) => BILL_META[t] || { color: "#6B7280", gradient: "linear-gradient(135deg,#9CA3AF,#6B7280)", label: t };
const getBillIcon  = (type, size = 28) => {
  const sx = { fontSize: size, color: "#fff" };
  return {
    [BACKEND_KEY[BILL_TYPES.ELECTRICITY]]: <ElectricBolt sx={sx} />,
    [BACKEND_KEY[BILL_TYPES.GAS]]: <LocalFireDepartment sx={sx} />,
    [BACKEND_KEY[BILL_TYPES.WATER]]: <Water sx={sx} />,
    [BACKEND_KEY[BILL_TYPES.MAINTENANCE]]: <Build sx={sx} />,
    [BACKEND_KEY[BILL_TYPES.SECURITY]]: <AccountBalance sx={sx} />,
    [BACKEND_KEY[BILL_TYPES.TRASH]]: <Delete sx={sx} />,
    [BACKEND_KEY[BILL_TYPES.RENT]]: <AccountBalance sx={sx} />,
    [BACKEND_KEY[BILL_TYPES.FIXED]]: <AccountBalance sx={sx} />,
  }[type] || <AccountBalance sx={sx} />;
};
// ─────────────────────────────────────────────────────────────────

export default function PendingBillLandlordPage() {
  title("Bill Management");
  const open = useSelector((s) => s.sidebar.open);
  const [loading,          setLoading]         = useState(true);
  const [data,             setData]            = useState();
  const location = useLocation();
  const [mylocation]                           = useState(location.pathname);
  const [rejectReason,     setRejectReason]    = useState("");
  const [activeTab,        setActiveTab]       = useState(0);
  const [expandedCards,    setExpandedCards]   = useState({});
  const [confirmDialog,    setConfirmDialog]   = useState({ open: false, billId: null, status: null, type: null, billTypeName: "", amount: null });
  const [reminderDialog,   setReminderDialog]  = useState({ open: false, bill: null, billTypeName: "" });
  const [reminderMsg,      setReminderMsg]     = useState("");
  const [reminderSending,  setReminderSending] = useState(false);

  const dispatch = useDispatch();
  useParams();
  const getId = window.location.pathname.replace("/pending-bill/", "");
  const [urlTenantId, urlFlatId] = getId.split("/");

  // ── Redux tenant ─────────────────────────────────────────────────
  const flats        = useSelector((s) => s?.validateUser?.UserValidate?.data?.user?.flats);
  const validateUSER = useSelector((s) => s?.validateUser?.UserValidate?.data);
  const reduxReqs    = validateUSER?.user?.requests || [];

  const displayTenant =
    reduxReqs.find((r) => String(r.tenantId) === String(urlTenantId) && String(r.flatId) === String(urlFlatId)) ||
    flats?.flatMap((i) => i.propertyDetail || [])?.find((p) => String(p._id) === String(urlFlatId) && String(p.tenantId) === String(urlTenantId));

  // ── Fetch ─────────────────────────────────────────────────────────
  React.useEffect(() => {
    setLoading(true);
    dispatch(GetBill({ id: getId })).then((res) => {
      setData(res?.payload?.data?.data);
      setLoading(false);
    });
  }, [dispatch, getId]);

  // ── All bills flat list ───────────────────────────────────────────
  const allBills = useMemo(() => {
    if (!data) return [];
    return [
      ...(data?.[BACKEND_KEY[BILL_TYPES.ELECTRICITY]] || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.ELECTRICITY] })),
      ...(data?.[BACKEND_KEY[BILL_TYPES.GAS]]         || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.GAS]         })),
      ...(data?.[BACKEND_KEY[BILL_TYPES.WATER]]       || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.WATER]       })),
      ...(data?.[BACKEND_KEY[BILL_TYPES.MAINTENANCE]] || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.MAINTENANCE] })),
      // Security bills are new (Phase 4) — this array will simply be empty
      // until the backend adds a matching `security` collection to GetBill.
      ...(data?.[BACKEND_KEY[BILL_TYPES.SECURITY]]    || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.SECURITY]    })),
      ...(data?.[BACKEND_KEY[BILL_TYPES.TRASH]]       || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.TRASH]       })),
      // Raised automatically each cycle: the rent bill and the combined
      // water + security + trash bill.
      ...(data?.[BACKEND_KEY[BILL_TYPES.RENT]]        || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.RENT]        })),
      ...(data?.[BACKEND_KEY[BILL_TYPES.FIXED]]       || []).map((b) => ({ ...b, billType: BACKEND_KEY[BILL_TYPES.FIXED]       })),
    ];
  }, [data]);

  const unpaidBills   = allBills.filter((b) => getBillCategory(b) === "unpaid"   && (daysUntilDue(b.dueDate) === null || daysUntilDue(b.dueDate) >= 0));
  const awaitingBills = allBills.filter((b) => getBillCategory(b) === "awaiting");
  const paidBills     = allBills.filter((b) => getBillCategory(b) === "paid");

  // ── Financial summary ─────────────────────────────────────────────
  const financials = useMemo(() => {
    const totalIssued  = allBills.reduce((s, b) => s + (parseFloat(b.billAmount) || 0), 0);
    const totalPaid    = paidBills.reduce((s, b) => s + (parseFloat(b?.tenantPayment?.amount || b.billAmount) || 0), 0);
    const totalPending = unpaidBills.reduce((s, b) => s + (parseFloat(b.billAmount) || 0), 0);
    const totalAwaiting= awaitingBills.reduce((s, b) => s + (parseFloat(b.billAmount) || 0), 0);
    const collectionRate = totalIssued > 0 ? Math.round((totalPaid / totalIssued) * 100) : 0;
    return { totalIssued, totalPaid, totalPending, totalAwaiting, collectionRate };
  }, [allBills, paidBills, unpaidBills, awaitingBills]);

  const urgentBills = unpaidBills.filter((b) => { const d = daysUntilDue(b.dueDate); return d !== null && d <= 2; });

  // ── Handlers ──────────────────────────────────────────────────────
  const toggleExpand = (id) => setExpandedCards((p) => ({ ...p, [id]: !p[id] }));

  const openConfirm = (billId, status, type, billTypeName, amount) => {
    setRejectReason("");
    setConfirmDialog({ open: true, billId, status, type, billTypeName, amount });
  };
  const closeConfirm = () => setConfirmDialog({ open: false, billId: null, status: null, type: null, billTypeName: "", amount: null });

  const handleConfirmAction = async () => {
    const { billId, status, type } = confirmDialog;
    closeConfirm();
    try {
      const token   = localStorage.getItem("token") || sessionStorage.getItem("ots_token");
      const payload = status === "rejected"
        ? { approvalStatus: status, rejectionReason: rejectReason }
        : { approvalStatus: status };
      const ep = {
        [BACKEND_KEY[BILL_TYPES.ELECTRICITY]]: `${BASE_URL}/approve-electric-bill/${billId}`,
        [BACKEND_KEY[BILL_TYPES.GAS]]:         `${BASE_URL}/approve-ssgc-bill/${billId}`,
        [BACKEND_KEY[BILL_TYPES.WATER]]:       `${BASE_URL}/approve-water-bill/${billId}`,
        [BACKEND_KEY[BILL_TYPES.MAINTENANCE]]: `${BASE_URL}/approve-maintanance-bill/${billId}`,
        // Proposed endpoint, unverified against a real backend (see plan notes).
        [BACKEND_KEY[BILL_TYPES.SECURITY]]:    `${BASE_URL}/approve-security-bill/${billId}`,
        [BACKEND_KEY[BILL_TYPES.TRASH]]:       `${BASE_URL}/approve-trash-bill/${billId}`,
        [BACKEND_KEY[BILL_TYPES.RENT]]:        `${BASE_URL}/approve-rent-bill/${billId}`,
        [BACKEND_KEY[BILL_TYPES.FIXED]]:       `${BASE_URL}/approve-fixed-charges-bill/${billId}`,
      };
      if (ep[type]) await axios.post(ep[type], payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Bill ${status === "approved" ? "approved ✅" : "rejected ❌"} successfully.`);
      setLoading(true);
      dispatch(GetBill({ id: getId })).then((res) => { setData(res?.payload?.data?.data); setLoading(false); });
    } catch { toast.error("Failed to update bill status. Please try again."); }
  };

  const openReminder  = (bill, name) => { setReminderMsg(""); setReminderDialog({ open: true, bill, billTypeName: name }); };
  const closeReminder = () => setReminderDialog({ open: false, bill: null, billTypeName: "" });

  const sendReminder = async () => {
    if (!reminderMsg.trim()) { toast.warning("Please enter a message for the tenant."); return; }
    setReminderSending(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("ots_token");
      await axios.post(
        `${BASE_URL}/send-bill-reminder`,
        { tenantId: urlTenantId, flatId: urlFlatId, billId: reminderDialog.bill?._id, billType: reminderDialog.billTypeName, dueDate: reminderDialog.bill?.dueDate, amount: reminderDialog.bill?.billAmount, message: reminderMsg.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment reminder sent to tenant successfully. 📨");
      closeReminder();
    } catch { toast.error("Failed to send reminder. Please try again.");
    } finally { setReminderSending(false); }
  };

  // ── BillCard ──────────────────────────────────────────────────────
  const BillCard = ({ bill, category }) => {
    const meta       = getBillMeta(bill.billType);
    const isExpanded = expandedCards[bill._id];
    const days       = daysUntilDue(bill.dueDate);
    const isUrgent   = category === "unpaid" && days !== null && days <= 2;
    const paidAmt    = bill?.tenantPayment?.amount || (bill?.createdBy?.role === "tenant" ? bill.billAmount : null);

    return (
      <Card
        sx={{
          borderRadius: "20px",
          border: isUrgent ? `1.5px solid ${meta.color}40` : "1px solid rgba(226,232,240,0.8)",
          boxShadow: isUrgent
            ? `0 4px 24px ${meta.color}20, 0 2px 8px rgba(0,0,0,0.06)`
            : "0 4px 20px rgba(5,8,15,0.08), 0 1px 4px rgba(5,8,15,0.04)",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "white",
          transition: "box-shadow 0.3s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          "&:hover": {
            boxShadow: `0 12px 48px rgba(5,8,15,0.14), 0 4px 12px rgba(5,8,15,0.08)`,
            transform: "translateY(-4px)",
          },
        }}
      >
        {/* Colored top bar */}
        <Box sx={{
          background: meta.gradient,
          px: 2.5,
          py: 2.2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-30px", right: "-30px",
            width: "100px", height: "100px",
            background: "rgba(255,255,255,0.08)",
            borderRadius: "50%",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            bottom: "-20px", left: "30%",
            width: "80px", height: "80px",
            background: "rgba(255,255,255,0.05)",
            borderRadius: "50%",
          },
        }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{
              background: "rgba(255,255,255,0.18)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "12px",
              p: "8px",
              display: "flex",
            }}>
              {getBillIcon(bill.billType, 22)}
            </Box>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1rem", fontFamily: "'Syne', sans-serif", letterSpacing: "-0.3px" }}>
                {meta.label}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.7rem", fontFamily: "monospace", letterSpacing: "0.5px" }}>
                #{bill._id.slice(-6).toUpperCase()}
              </Typography>
            </Box>
          </Box>
          <Box textAlign="right" sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.4rem", lineHeight: 1, fontFamily: "'Syne', sans-serif", letterSpacing: "-0.5px" }}>
              {fmtCurrency(bill.billAmount)}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.68rem", letterSpacing: "0.4px", textTransform: "uppercase", mt: 0.3 }}>Bill Amount</Typography>
          </Box>
        </Box>

        <CardContent sx={{ flex: 1, p: 2.5 }}>
          {/* Status + due badge */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            {category === "unpaid"   && (
              <Chip label="Awaiting Payment" size="small"
                sx={{ bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700, fontSize: "0.7rem",
                  border: "1px solid #FDE68A", borderRadius: "8px" }} />
            )}
            {category === "awaiting" && (
              <Chip label="Verify Payment" size="small"
                sx={{ bgcolor: "#EFF6FF", color: "#1D4ED8", fontWeight: 700, fontSize: "0.7rem",
                  border: "1px solid #BFDBFE", borderRadius: "8px" }} />
            )}
            {category === "paid" && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: "13px !important", color: "#065F46 !important" }} />}
                label="Paid & Verified" size="small"
                sx={{ bgcolor: "#ECFDF5", color: "#065F46", fontWeight: 700, fontSize: "0.7rem",
                  border: "1px solid #A7F3D0", borderRadius: "8px" }} />
            )}

            {/* Due date pill */}
            {days !== null && category !== "paid" && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1.5, py: 0.5, borderRadius: "20px",
                bgcolor: days === 0 ? "#FEF2F2" : days <= 2 ? "#FFFBEB" : "#F8FAFC",
                border: `1px solid ${days === 0 ? "#FECACA" : days <= 2 ? "#FDE68A" : "#E2E8F0"}`,
              }}>
                <AccessTime sx={{ fontSize: 12, color: days <= 2 ? "#B45309" : "#94A3B8" }} />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: days === 0 ? "#B91C1C" : days <= 2 ? "#B45309" : "#64748B" }}>
                  {days === 0 ? "Due Today" : days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Urgent alert */}
          {isUrgent && (
            <Alert
              severity={days === 0 ? "error" : "warning"}
              icon={<NotificationsActive fontSize="small" />}
              sx={{ mb: 2, borderRadius: "12px", fontSize: "0.78rem", py: 0.5,
                "& .MuiAlert-message": { fontWeight: 500 } }}
            >
              {days === 0
                ? "This bill is due today. Send an immediate payment reminder to the tenant."
                : `Due in ${days} day${days === 1 ? "" : "s"}. You may now send a payment reminder to the tenant.`}
            </Alert>
          )}

          {/* Upcoming reminder hint — visible when >2 days left */}
          {category === "unpaid" && days !== null && days > 2 && (
            <Box sx={{
              display: "flex", alignItems: "flex-start", gap: 1.2,
              p: 1.8, bgcolor: "#F8FAFC",
              border: "1.5px dashed #CBD5E1",
              borderRadius: "12px", mb: 2,
            }}>
              <Schedule sx={{ fontSize: 15, color: "#94A3B8", mt: "2px", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.75rem", color: "#64748B", lineHeight: 1.6 }}>
                <strong style={{ color: "#475569" }}>Reminder available</strong> in {days - 2} day{days - 2 === 1 ? "" : "s"}, when the due date is within 2 days.
              </Typography>
            </Box>
          )}

          {/* Awaiting note */}
          {category === "awaiting" && (
            <Alert severity="info" icon={<HourglassTop fontSize="small" />}
              sx={{ mb: 2, borderRadius: "12px", fontSize: "0.78rem", py: 0.5 }}>
              Tenant submitted a payment receipt. Review and approve or reject below.
            </Alert>
          )}

          {/* Paid success */}
          {category === "paid" && (
            <Alert severity="success" icon={<VerifiedUser fontSize="small" />}
              sx={{ mb: 2, borderRadius: "12px", fontSize: "0.78rem", py: 0.5 }}>
              Payment verified and approved. No further action required.
            </Alert>
          )}

          <Divider sx={{ borderColor: "#F1F5F9", mb: 2 }} />

          {/* Dates row */}
          <Grid container spacing={1.5} mb={1.5}>
            <Grid item xs={6}>
              <Box sx={{ p: 1.5, bgcolor: "#F8FAFC", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
                <Typography sx={{ fontSize: "0.62rem", color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", mb: 0.4 }}>
                  Issue Date
                </Typography>
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#1E293B", letterSpacing: "-0.2px" }}>
                  {moment(bill.billDate).format("DD MMM YYYY")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ p: 1.5, bgcolor: isUrgent ? "#FFFBEB" : "#F8FAFC", borderRadius: "12px", border: `1px solid ${isUrgent ? "#FDE68A" : "#E2E8F0"}` }}>
                <Typography sx={{ fontSize: "0.62rem", color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", mb: 0.4 }}>
                  Due Date
                </Typography>
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: isUrgent ? "#B45309" : "#1E293B", letterSpacing: "-0.2px" }}>
                  {bill.dueDate || "—"}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Payment breakdown */}
          <Box sx={{ p: 1.8, bgcolor: "#F8FAFC", borderRadius: "12px", border: "1px solid #E2E8F0", mb: 1.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8", fontWeight: 500 }}>Billed Amount</Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#1E293B" }}>{fmtCurrency(bill.billAmount)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={paidAmt ? 1 : 0}>
              <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8", fontWeight: 500 }}>Tenant Paid</Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: paidAmt ? "#059669" : "#F43F5E" }}>
                {paidAmt ? fmtCurrency(paidAmt) : "Not Paid"}
              </Typography>
            </Box>
            {paidAmt && (
              <>
                <Divider sx={{ borderColor: "#E2E8F0", my: 1 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8", fontWeight: 500 }}>Balance</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: (parseFloat(bill.billAmount) - parseFloat(paidAmt)) <= 0 ? "#059669" : "#F43F5E" }}>
                    {fmtCurrency(Math.max(0, parseFloat(bill.billAmount) - parseFloat(paidAmt)))}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Expandable */}
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Divider sx={{ borderColor: "#F1F5F9", mb: 2 }} />
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#94A3B8", mb: 1.5, textTransform: "uppercase", letterSpacing: "1px" }}>
              Bill Images
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              {bill.billImages?.landlord?.image?.trim() && (
                <Box>
                  <a href={bill.billImages.landlord.image} target="_blank" rel="noreferrer">
                    <img src={bill.billImages.landlord.image} alt="Landlord Bill" style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 12, cursor: "pointer", border: "2px solid #E5E7EB" }} />
                  </a>
                  <Typography sx={{ fontSize: "0.65rem", textAlign: "center", mt: 0.5, color: "#94A3B8", fontWeight: 600 }}>Landlord</Typography>
                </Box>
              )}
              {bill.billImages?.tenant?.image?.trim() && (
                <Box>
                  <a href={bill.billImages.tenant.image} target="_blank" rel="noreferrer">
                    <img src={bill.billImages.tenant.image} alt="Tenant Receipt" style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 12, cursor: "pointer", border: "2px solid #10B981" }} />
                  </a>
                  <Typography sx={{ fontSize: "0.65rem", textAlign: "center", mt: 0.5, color: "#10B981", fontWeight: 700 }}>Tenant Receipt ✓</Typography>
                </Box>
              )}
              {!bill.billImages?.landlord?.image && !bill.billImages?.tenant?.image && (
                <Typography sx={{ fontSize: "0.8rem", color: "#CBD5E1" }}>No images attached.</Typography>
              )}
            </Box>

            {bill.tenantMessage?.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#94A3B8", mb: 1, textTransform: "uppercase", letterSpacing: "1px" }}>
                  Tenant Note
                </Typography>
                {bill.tenantMessage.map((msg, i) => (
                  <Box key={i} sx={{ bgcolor: "#F0FDF4", border: "1px solid #BBF7D0", p: 1.5, borderRadius: "10px", mb: 1 }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#166534" }}>{msg}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {category === "paid" && bill.approval?.approvedAt && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: "10px" }}>
                <Typography sx={{ fontSize: "0.75rem", color: "#065F46", fontWeight: 700 }}>
                  ✅ Approved on {moment(bill.approval.approvedAt).format("DD MMM YYYY [at] hh:mm A")}
                </Typography>
              </Box>
            )}
          </Collapse>

          <Box mt={1.5}>
            <Button
              size="small"
              onClick={() => toggleExpand(bill._id)}
              endIcon={isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
              sx={{ fontSize: "0.78rem", color: "#94A3B8", textTransform: "none", p: "4px 8px",
                fontWeight: 600, borderRadius: "8px",
                "&:hover": { color: "#3B82F6", bgcolor: "rgba(59,130,246,0.06)" } }}
            >
              {isExpanded ? "Show Less" : "View Details"}
            </Button>
          </Box>
        </CardContent>

        {/* Actions */}
        {(category === "unpaid" && isUrgent) || category === "awaiting" || category === "paid" ? (
          <CardActions sx={{ p: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
            {category === "unpaid" && isUrgent && (
              <Button
                variant="contained"
                fullWidth
                startIcon={<NotificationsActive />}
                onClick={() => openReminder(bill, meta.label)}
                sx={{
                  fontWeight: 700, borderRadius: "12px", textTransform: "none",
                  py: 1.2, fontSize: "0.88rem",
                  background: "linear-gradient(135deg,#FF8C42,#F97316,#EA580C)",
                  boxShadow: "0 4px 16px rgba(249,115,22,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg,#EA580C,#C2410C)",
                    boxShadow: "0 8px 24px rgba(249,115,22,0.5)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                Send Payment Reminder
              </Button>
            )}

            {category === "awaiting" && (
              <Box display="flex" gap={1.2} width="100%">
                <Button variant="contained" color="success" fullWidth startIcon={<CheckCircle />}
                  onClick={() => openConfirm(bill._id, "approved", bill.billType, meta.label, bill.billAmount)}
                  sx={{ fontWeight: 700, borderRadius: "12px", textTransform: "none", py: 1.1,
                    background: "linear-gradient(135deg,#059669,#10B981)",
                    boxShadow: "0 4px 16px rgba(16,185,129,0.3)",
                    "&:hover": { boxShadow: "0 6px 20px rgba(16,185,129,0.45)", transform: "translateY(-1px)" },
                    transition: "all 0.2s",
                  }}>
                  Approve
                </Button>
                <Button variant="contained" color="error" fullWidth startIcon={<Cancel />}
                  onClick={() => openConfirm(bill._id, "rejected", bill.billType, meta.label, bill.billAmount)}
                  sx={{ fontWeight: 700, borderRadius: "12px", textTransform: "none", py: 1.1,
                    background: "linear-gradient(135deg,#DC2626,#F43F5E)",
                    boxShadow: "0 4px 16px rgba(244,63,94,0.3)",
                    "&:hover": { boxShadow: "0 6px 20px rgba(244,63,94,0.45)", transform: "translateY(-1px)" },
                    transition: "all 0.2s",
                  }}>
                  Reject
                </Button>
              </Box>
            )}

            {category === "paid" && (
              <Chip icon={<CheckCircle />} label="Payment Verified & Approved" color="success"
                sx={{ width: "100%", fontWeight: 700, borderRadius: "12px", height: 38,
                  background: "linear-gradient(135deg,#059669,#10B981)",
                  color: "white",
                  "& .MuiChip-icon": { color: "white !important" },
                }} />
            )}
          </CardActions>
        ) : null}
      </Card>
    );
  };

  // ── Tab label helper ──────────────────────────────────────────────
  const TabLabel = ({ icon, label, count, badgeColor }) => (
    <Box display="flex" alignItems="center" gap={1}>
      {icon}
      <span>{label}</span>
      {count > 0 && (
        <Box sx={{
          minWidth: 20, height: 20, borderRadius: "10px", bgcolor: badgeColor,
          display: "flex", alignItems: "center", justifyContent: "center", px: 0.8,
          boxShadow: `0 2px 6px ${badgeColor}60`,
        }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: "#fff" }}>{count}</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Wrapper open={open} mylocation={mylocation} />
      <div className={`bill-management-container ${open ? "sidebar-open" : "sidebar-closed"}`}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 2 }}>
            <CircularProgress size={52} thickness={4} sx={{ color: "#3B82F6" }} />
            <Typography sx={{ color: "#94A3B8", fontWeight: 500, fontFamily: "'Outfit', sans-serif" }}>Loading bill records…</Typography>
          </Box>
        ) : (
          <Container maxWidth="xl" sx={{ py: 4 }}>

            {/* Page header */}
            <Box mb={4}>
              <Typography sx={{
                fontWeight: 900, fontSize: "2rem", color: "#0F172A",
                fontFamily: "'Syne', sans-serif", letterSpacing: "-0.8px",
                background: "linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                Utility Bill Management
              </Typography>
              <Typography sx={{ color: "#94A3B8", mt: 0.5, fontSize: "0.9rem", fontWeight: 400 }}>
                Complete overview of all utility bills issued to this tenant — track payment status, collection progress, and send reminders.
              </Typography>
            </Box>

            {/* Tenant Banner */}
            <TenantInfoBanner tenant={displayTenant} />

            {/* ── Financial Summary ── */}
            <Box sx={{
              background: "linear-gradient(145deg, #090e1a 0%, #0f1829 50%, #0a1520 100%)",
              borderRadius: "24px",
              p: 3.5,
              mb: 3,
              border: "1px solid rgba(255,255,255,0.06)",
              boxShadow: "0 24px 64px rgba(0,0,0,0.22), 0 0 0 1px rgba(255,255,255,0.04)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "-60px", right: "-60px",
                width: "220px", height: "220px",
                background: "radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 60%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: "-40px", left: "20%",
                width: "160px", height: "160px",
                background: "radial-gradient(circle, rgba(16,185,129,0.07) 0%, transparent 60%)",
                pointerEvents: "none",
              },
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Box sx={{ width: 3, height: 20, bgcolor: "#3B82F6", borderRadius: "2px" }} />
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>
                  Financial Overview — This Tenant
                </Typography>
              </Box>
              <Grid container spacing={2} mb={3}>
                {[
                  { label: "Total Issued",          value: fmtCurrency(financials.totalIssued),   sub: `${allBills.length} bill${allBills.length !== 1 ? "s" : ""}`,        color: "#CBD5E1", accent: "rgba(203,213,225,0.1)" },
                  { label: "Amount Collected",      value: fmtCurrency(financials.totalPaid),     sub: `${paidBills.length} bill${paidBills.length !== 1 ? "s" : ""} paid`,  color: "#34D399", accent: "rgba(52,211,153,0.1)"  },
                  { label: "Pending Collection",    value: fmtCurrency(financials.totalPending),  sub: `${unpaidBills.length} unpaid`,                                       color: "#FCD34D", accent: "rgba(252,211,77,0.1)"  },
                  { label: "Awaiting Verification", value: fmtCurrency(financials.totalAwaiting), sub: `${awaitingBills.length} to review`,                                 color: "#60A5FA", accent: "rgba(96,165,250,0.1)"  },
                ].map((item) => (
                  <Grid item xs={6} sm={3} key={item.label}>
                    <Box sx={{
                      p: 2, borderRadius: "16px",
                      background: item.accent,
                      border: `1px solid ${item.color}20`,
                      position: "relative", zIndex: 1,
                    }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.8px", mb: 0.8, fontWeight: 700 }}>{item.label}</Typography>
                      <Typography sx={{ color: item.color, fontWeight: 900, fontSize: "1.3rem", fontFamily: "'Syne', sans-serif", lineHeight: 1, letterSpacing: "-0.5px" }}>{item.value}</Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: "0.7rem", mt: 0.5, fontWeight: 500 }}>{item.sub}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Collection progress bar */}
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                  <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.5px" }}>
                    Collection Rate
                  </Typography>
                  <Typography sx={{ color: "#34D399", fontWeight: 900, fontSize: "1rem", fontFamily: "'Syne', sans-serif" }}>
                    {financials.collectionRate}%
                  </Typography>
                </Box>
                <Box sx={{ height: 8, bgcolor: "rgba(255,255,255,0.06)", borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.04)" }}>
                  <Box sx={{
                    height: "100%", width: `${financials.collectionRate}%`,
                    background: "linear-gradient(90deg, #059669, #10B981, #34D399)",
                    borderRadius: "8px",
                    transition: "width 1s cubic-bezier(0.4,0,0.2,1)",
                    boxShadow: "0 0 12px rgba(52,211,153,0.4)",
                  }} />
                </Box>
                <Typography sx={{ color: "rgba(255,255,255,0.22)", fontSize: "0.68rem", mt: 0.8, fontWeight: 500 }}>
                  {fmtCurrency(financials.totalPaid)} collected out of {fmtCurrency(financials.totalIssued)} total
                </Typography>
              </Box>
            </Box>

            {/* Reminder feature notice — always visible */}
            <Alert
              severity="info"
              icon={<NotificationsActive fontSize="small" />}
              sx={{ mb: 3, borderRadius: "16px", border: "1px solid #BFDBFE",
                background: "linear-gradient(135deg, #EFF6FF, #F0F9FF)",
                boxShadow: "0 4px 16px rgba(59,130,246,0.08)",
                "& .MuiAlert-message": { width: "100%" } }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#1E40AF" }}>
                    Payment Reminder Feature Available
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "#60A5FA", mt: 0.2 }}>
                    For any unpaid bill with <strong>2 days or less</strong> until due, you can send a direct reminder via email and in-app notification.
                    {urgentBills.length > 0
                      ? ` — ${urgentBills.length} bill${urgentBills.length > 1 ? "s are" : " is"} eligible right now.`
                      : " No bills are eligible yet."}
                  </Typography>
                </Box>
                {urgentBills.length > 0 && (
                  <Chip
                    label={`${urgentBills.length} Reminder${urgentBills.length > 1 ? "s" : ""} Available`}
                    color="warning"
                    size="small"
                    icon={<NotificationsActive />}
                    sx={{ fontWeight: 700, borderRadius: "10px" }}
                  />
                )}
              </Box>
            </Alert>

            {/* Tabs */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                background: "white",
                borderRadius: "16px 16px 0 0",
                border: "1px solid #E2E8F0",
                borderBottom: "none",
                px: 1,
              }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{
                    minHeight: 56,
                    "& .MuiTab-root": {
                      fontWeight: 600, textTransform: "none", fontSize: "0.9rem",
                      color: "#94A3B8", minHeight: 56, px: 2.5,
                      fontFamily: "'Outfit', sans-serif",
                      transition: "color 0.2s",
                    },
                    "& .Mui-selected": { color: "#0F172A !important", fontWeight: 800 },
                    "& .MuiTabs-indicator": {
                      background: "linear-gradient(90deg, #3B82F6, #6366F1)",
                      height: "3px", borderRadius: "3px 3px 0 0",
                    },
                  }}
                >
                  <Tab label={<TabLabel icon={<MoneyOff fontSize="small" />}     label="Unpaid Bills"          count={unpaidBills.length}   badgeColor="#F59E0B" />} />
                  <Tab label={<TabLabel icon={<HourglassTop fontSize="small" />}  label="Awaiting Verification" count={awaitingBills.length} badgeColor="#3B82F6" />} />
                  <Tab label={<TabLabel icon={<VerifiedUser fontSize="small" />}  label="Paid & Verified"       count={paidBills.length}     badgeColor="#10B981" />} />
                </Tabs>
              </Box>
              <Box sx={{ height: 1, bgcolor: "#E2E8F0" }} />
            </Box>

            {/* Tab content */}
            {[unpaidBills, awaitingBills, paidBills].map((list, tabIdx) => (
              activeTab === tabIdx && (
                list.length > 0 ? (
                  <Grid container spacing={3} key={tabIdx}>
                    {list.map((bill) => (
                      <Grid item xs={12} md={6} lg={4} key={bill._id}>
                        <BillCard bill={bill} category={["unpaid","awaiting","paid"][tabIdx]} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box key={tabIdx} sx={{
                    textAlign: "center", py: 12,
                    background: "white",
                    borderRadius: "0 0 20px 20px",
                    border: "1px solid #E2E8F0",
                    borderTop: "none",
                  }}>
                    {tabIdx === 0 && <CheckCircle    sx={{ fontSize: 72, color: "#D1FAE5", mb: 2 }} />}
                    {tabIdx === 1 && <HourglassTop   sx={{ fontSize: 72, color: "#DBEAFE", mb: 2 }} />}
                    {tabIdx === 2 && <VerifiedUser   sx={{ fontSize: 72, color: "#D1FAE5", mb: 2 }} />}
                    <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: "#CBD5E1", mb: 0.5, fontFamily: "'Syne', sans-serif" }}>
                      {["No unpaid bills at this time.", "No payments awaiting verification.", "No verified payments yet."][tabIdx]}
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: "#E2E8F0", fontWeight: 400 }}>
                      {["All issued bills are either paid or pending tenant action.", "Once a tenant submits a payment receipt, it will appear here.", "Bills you approve will appear here as verified payment records."][tabIdx]}
                    </Typography>
                  </Box>
                )
              )
            ))}

          </Container>
        )}
      </div>

      {/* ── Reminder Dialog ──────────────────────────────────────────── */}
      <Dialog open={reminderDialog.open} onClose={closeReminder} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.2)" } }}>
        <Box sx={{
          background: "linear-gradient(135deg,#FF8C42 0%,#F97316 50%,#EA580C 100%)",
          p: 3, position: "relative", overflow: "hidden",
          "&::after": { content: '""', position: "absolute", top: "-30px", right: "-30px",
            width: "120px", height: "120px", background: "rgba(255,255,255,0.08)", borderRadius: "50%" },
        }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ position: "relative", zIndex: 1 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 46, height: 46, backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.3)" }}>
              <NotificationsActive sx={{ color: "#fff" }} />
            </Avatar>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.1rem", fontFamily: "'Syne', sans-serif" }}>Send Payment Reminder</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.78rem" }}>
                Tenant will receive an email &amp; in-app notification
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ p: 2.2, background: "linear-gradient(135deg, #fff7ed, #fffbeb)", border: "1px solid #fed7aa", borderRadius: "14px", mb: 3 }}>
            <Grid container spacing={1.5}>
              {[
                ["Bill Type",   reminderDialog.billTypeName],
                ["Amount Due",  fmtCurrency(reminderDialog.bill?.billAmount)],
                ["Due Date",    reminderDialog.bill?.dueDate || "—"],
              ].map(([k, v]) => (
                <Grid item xs={4} key={k}>
                  <Typography sx={{ fontSize: "0.62rem", color: "#92400E", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>{k}</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "#78350F", letterSpacing: "-0.2px" }}>{v}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#374151", mb: 1 }}>
            Your message to the tenant <span style={{ color: "#EF4444" }}>*</span>
          </Typography>
          <TextField
            fullWidth multiline minRows={4}
            placeholder={`e.g., Dear Tenant, this is a friendly reminder that your ${reminderDialog.billTypeName || "utility"} bill of ${fmtCurrency(reminderDialog.bill?.billAmount)} is due on ${reminderDialog.bill?.dueDate || "the due date"}. Kindly ensure timely payment. Thank you.`}
            value={reminderMsg}
            onChange={(e) => setReminderMsg(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", fontSize: "0.88rem",
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FDA172" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#F97316", borderWidth: "2px" },
            } }}
          />
          <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mt: 1 }}>
            💡 A personalised reminder will be sent via email and in-app notification to the tenant.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 1.2 }}>
          <Button onClick={closeReminder} variant="outlined" disabled={reminderSending}
            sx={{ borderRadius: "12px", textTransform: "none", minWidth: 90, fontWeight: 600,
              borderColor: "#E2E8F0", color: "#64748B",
              "&:hover": { borderColor: "#CBD5E1", bgcolor: "#F8FAFC" } }}>
            Cancel
          </Button>
          <Button onClick={sendReminder} variant="contained" disabled={!reminderMsg.trim() || reminderSending}
            startIcon={reminderSending ? <CircularProgress size={16} color="inherit" /> : <NotificationsActive />}
            sx={{ borderRadius: "12px", textTransform: "none", minWidth: 160, fontWeight: 700,
              background: "linear-gradient(135deg,#F97316,#EA580C)",
              boxShadow: "0 4px 16px rgba(249,115,22,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#EA580C,#C2410C)", boxShadow: "0 6px 24px rgba(249,115,22,0.5)" },
              "&:disabled": { opacity: 0.6 },
            }}>
            {reminderSending ? "Sending…" : "Send Reminder"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Dialog ───────────────────────────────────────────── */}
      <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "24px", boxShadow: "0 32px 80px rgba(0,0,0,0.2)" } }}>
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{
              bgcolor: confirmDialog.status === "approved" ? "#ECFDF5" : "#FFF1F2",
              width: 48, height: 48,
              border: `2px solid ${confirmDialog.status === "approved" ? "#A7F3D0" : "#FECDD3"}`,
            }}>
              {confirmDialog.status === "approved"
                ? <CheckCircle sx={{ color: "#059669" }} />
                : <Cancel sx={{ color: "#E11D48" }} />}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", fontFamily: "'Syne', sans-serif", letterSpacing: "-0.3px" }}>
                Confirm {confirmDialog.status === "approved" ? "Approval" : "Rejection"}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "#94A3B8" }}>Please review the details before proceeding.</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box sx={{ p: 2, background: "linear-gradient(135deg, #F8FAFC, #F1F5F9)", border: "1px solid #E2E8F0", borderRadius: "14px", mb: 2 }}>
            <Typography sx={{ fontSize: "0.82rem", color: "#64748B", mb: 0.5 }}><strong style={{ color: "#334155" }}>Bill Type:</strong> {confirmDialog.billTypeName}</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#64748B" }}><strong style={{ color: "#334155" }}>Amount:</strong> {fmtCurrency(confirmDialog.amount)}</Typography>
          </Box>
          {confirmDialog.status === "rejected" && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#374151", mb: 1 }}>
                Reason for rejection <span style={{ color: "#94A3B8", fontWeight: 400 }}>(optional)</span>
              </Typography>
              <TextField fullWidth multiline minRows={3} placeholder="Provide a reason so the tenant understands the rejection…"
                value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#F43F5E" } } }} />
            </Box>
          )}
          <Typography sx={{ fontSize: "0.72rem", color: "#CBD5E1", mt: 2, fontStyle: "italic" }}>
            This action will update the bill status and the tenant will be notified immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 1.2 }}>
          <Button onClick={closeConfirm} variant="outlined"
            sx={{ borderRadius: "12px", textTransform: "none", minWidth: 90, fontWeight: 600,
              borderColor: "#E2E8F0", color: "#64748B" }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} variant="contained"
            color={confirmDialog.status === "approved" ? "success" : "error"}
            sx={{ borderRadius: "12px", textTransform: "none", minWidth: 160, fontWeight: 700,
              background: confirmDialog.status === "approved"
                ? "linear-gradient(135deg,#059669,#10B981)"
                : "linear-gradient(135deg,#DC2626,#F43F5E)",
              boxShadow: confirmDialog.status === "approved"
                ? "0 4px 16px rgba(16,185,129,0.35)"
                : "0 4px 16px rgba(244,63,94,0.35)",
            }}>
            Yes, {confirmDialog.status === "approved" ? "Approve Payment" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}